import type { ToolCallMessagePart } from '@assistant-ui/react';
import { Loader } from '@mantine/core';

import classes from './ProgressBubble.module.css';
import { Bubble } from '#plugin-chat/components/bubble/Bubble.tsx';
import { Shimmer } from '#plugin-chat/components/shared/Shimmer.tsx';
import { resolveSlot } from '#plugin-chat/components/tool-display/resolve-spec.tsx';
import { useToolDisplaySpec } from '#plugin-chat/hooks/useToolDisplaySpec.ts';

interface ProgressBubbleProps {
  // biome-ignore lint/suspicious/noExplicitAny: Runtime part — per-tool types are erased at this layer.
  toolPart?: ToolCallMessagePart<any, any>;
  fallbackComponent?: React.ComponentType | null;
}

/**
 * Activity indicator rendered for the currently running tool in a collapsible run.
 *
 * Shape: "AiPSO Agent is {progress label}". The progress label is read off
 * the running tool's `display.progress` on its model-context entry (attached
 * by the `<Tool>` component). `display.progress` is either a `ReactNode` (a
 * static label) or a function `({ args }) => ReactNode` (args-derived).
 * Falls back to {@link GenericProgress} when the tool entry has no
 * `display.progress` — non-collapsible tools never reach this path in
 * normal flow, but the fallback keeps the bubble well-defined if they do.
 */
export function ProgressBubble({
  toolPart,
  fallbackComponent: FallbackComponent = GenericProgress,
}: ProgressBubbleProps) {
  const progress = useToolDisplaySpec(toolPart?.toolName)?.progress;
  const resolved = resolveSlot(progress, { args: toolPart?.args ?? {} });
  const renderedProgress = resolved ?? (FallbackComponent ? <FallbackComponent /> : null);

  return (
    <Bubble corners={[]} role="assistant">
      <div className={classes.loader}>
        <Loader color="dark.1" size="xs" type="dots" />
        {!!renderedProgress && <Shimmer className={classes.label}>AiPSO Agent is {renderedProgress}</Shimmer>}
      </div>
    </Bubble>
  );
}

function GenericProgress() {
  return <>thinking...</>;
}
